import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS } from "@/data/site";
import Logo from "@/components/Logo";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#FAFAF7]/70 backdrop-blur-xl border-b border-zinc-200" : "bg-transparent"
      }`}
    >
      <div className="container-tight flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="group">
          <Logo showWordmark={false} size="h-9 md:h-10" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              end={l.to === "/"}
              className={({ isActive }) =>
                `px-3 py-2 text-sm tracking-tight transition-colors ${
                  isActive ? "text-zinc-900" : "text-zinc-600 hover:text-zinc-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/contact"
            data-testid="nav-cta-start"
            className="hidden sm:inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-700 transition-colors"
          >
            Start a project
            <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-toggle"
            aria-label="Toggle menu"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-zinc-300 text-zinc-700 hover:text-zinc-900"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            data-testid="nav-mobile-panel"
            className="lg:hidden bg-[#FAFAF7]/95 backdrop-blur-xl border-t border-zinc-200"
          >
            <div className="container-tight py-6 flex flex-col">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-${l.testid}`}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `py-3 text-base border-b border-zinc-200 last:border-b-0 transition-colors ${
                      isActive ? "text-zinc-900" : "text-zinc-700 hover:text-zinc-900"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta"
                className="mt-6 inline-flex items-center justify-center gap-2 bg-zinc-900 text-white px-4 py-3 text-sm font-medium"
              >
                Start a project <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
